import { create } from 'zustand';
import { tokenStorage } from '@shared/api/token-storage';
import type { AdminUser, Session } from '@features/auth/model/auth.types';

export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  admin: AdminUser | null;
  setSession: (session: Session) => void;
  setAdmin: (admin: AdminUser) => void;
  clear: () => void;
}

/**
 * Holds only who is signed in. The token itself lives in `tokenStorage` and is
 * attached by the axios interceptor, so no component ever handles it directly.
 *
 * `status` starts as `restoring` when a token is already present: the app must
 * validate it against `/auth/me` before deciding what to render, otherwise a
 * refresh would flash the login page for an admin who is still signed in.
 */
export const useAuthStore = create<AuthState>((set) => ({
  status: tokenStorage.read() ? 'restoring' : 'anonymous',
  admin: null,

  setSession: (session) => {
    tokenStorage.write({ accessToken: session.accessToken, expiresAt: session.expiresAt });
    set({ status: 'authenticated', admin: session.admin });
  },

  setAdmin: (admin) => set({ status: 'authenticated', admin }),

  clear: () => {
    tokenStorage.clear();
    set({ status: 'anonymous', admin: null });
  },
}));

/** Lets non-React code (the axios interceptor) end the session on a 401. */
export function clearAuthSession(): void {
  useAuthStore.getState().clear();
}
